import 'react-native-gesture-handler';
import React, { useState, useEffect, useRef } from 'react';
import { FontDisplay, useFonts } from 'expo-font';
import { Ionicons } from '@expo/vector-icons';
import { NavigationContainer } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { View, ActivityIndicator, Platform, StyleSheet, StatusBar } from 'react-native';
import { Text } from './src/components/AppText';
import type { Session } from '@supabase/supabase-js';
import { supabase, DEFAULT_HOUSE_ID } from './src/lib/supabase';
import { TabNavigator } from './src/navigation/TabNavigator';
import { AuthScreen } from './src/screens/AuthScreen';
import { PhoneShell, useOnlineStatus, useWebViewportReady } from './src/components/AppShell';
import { OfflineBanner } from './src/components/Feedback';
import { useStore } from './src/store/useStore';
import { Colors, FONT_SOURCES } from './src/theme';
import { checkAndNotifyUpcomingExpenses } from './src/lib/notifications';

const ICON_FONT_SOURCES = Object.fromEntries(
  [...Object.entries(Ionicons.font), ...Object.entries(FONT_SOURCES)]
    .map(([family, source]) => [family, { uri: source, display: FontDisplay.SWAP }]),
) as Record<string, { uri: number; display: FontDisplay }>;

const AUTH_RESTORE_TIMEOUT_MS = 8000;

type ChromeState = {
  barStyle: 'light-content' | 'dark-content';
  backgroundColor: string;
};

const AUTH_CHROME: ChromeState = { barStyle: 'light-content', backgroundColor: Colors.dark };
const LIGHT_CHROME: ChromeState = { barStyle: 'dark-content', backgroundColor: Colors.bg };
const CARDS_CHROME: ChromeState = LIGHT_CHROME;

function setWebChromeColor(color: string) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', color);
  // iOS standalone PWAs paint the status-bar inset from the document canvas,
  // not only from the React view. Keep every outer layer synchronized with
  // the active screen so no contrasting strip appears above or below it.
  // The top and bottom insets should follow the active app canvas so the PWA
  // does not expose a contrasting strip around the authenticated shell.
  const documentSurface = color;
  document.documentElement.style.backgroundColor = documentSurface;
  document.body.style.backgroundColor = documentSurface;
  const root = document.getElementById('root');
  if (root) root.style.backgroundColor = documentSurface;

  const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  if (appleMeta) appleMeta.setAttribute('content', 'black-translucent');
  else {
    const meta = document.createElement('meta');
    meta.name = 'apple-mobile-web-app-status-bar-style';
    meta.content = 'black-translucent';
    document.head.appendChild(meta);
  }
}

function chromeForRoute(routeName?: string): ChromeState {
  return routeName ? LIGHT_CHROME : AUTH_CHROME;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [chrome, setChrome] = useState<ChromeState>(LIGHT_CHROME);
  const viewportReady = useWebViewportReady();
  const online = useOnlineStatus();
  const isOffline = useStore(state => state.isOffline);
  const lastSyncedAt = useStore(state => state.lastSyncedAt);
  const storeLoading = useStore(state => state.loading);
  const previousOnline = useRef(online);

  const [fontsLoaded] = useFonts(ICON_FONT_SOURCES);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    // Suppress focus outlines on web
    const style = document.createElement('style');
    style.textContent = [
      'input, textarea, button, [role="button"], [tabindex] { outline: none !important; }',
      '*:focus { outline: none !important; }',
    ].join('\n');
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    let mounted = true;
    let sessionResolved = false;
    const timeoutId = setTimeout(() => {
      if (!mounted || sessionResolved) return;
      sessionResolved = true;
      setLoading(false);
    }, AUTH_RESTORE_TIMEOUT_MS);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        sessionResolved = true;
        setSession(session);
        setLoading(false);
      })
      .catch((error) => {
        if (!mounted) return;
        sessionResolved = true;
        console.warn('[auth] Could not restore session:', error);
        setLoading(false);
      })
      .finally(() => clearTimeout(timeoutId));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setSession(session);
    });

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;

    const preventMultiTouchZoom = (event: Event) => {
      const touchEvent = event as TouchEvent;
      if (touchEvent.touches?.length > 1) event.preventDefault();
    };
    const preventGestureZoom = (event: Event) => event.preventDefault();
    const preventCtrlWheelZoom = (event: WheelEvent) => {
      if (event.ctrlKey) event.preventDefault();
    };
    document.addEventListener('touchmove', preventMultiTouchZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom, { passive: false });
    document.addEventListener('gesturechange', preventGestureZoom, { passive: false });
    document.addEventListener('gestureend', preventGestureZoom, { passive: false });
    document.addEventListener('wheel', preventCtrlWheelZoom, { passive: false });

    const style = document.createElement('style');
    style.textContent = `
      html, body, #root {
        width: 100%;
        max-width: 100vw;
        min-width: 0;
        min-height: 100%;
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        overflow-x: hidden;
        touch-action: pan-y;
      }
      body {
        width: 100%;
        min-height: 100dvh;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior-y: none;
        overscroll-behavior-x: none;
        color-scheme: light;
        -webkit-font-smoothing: antialiased;
        background: ${Colors.bg};
      }
      #root {
        width: 100%;
        max-width: 100vw;
        display: flex;
        flex-direction: column;
        min-height: 100dvh;
        align-items: center;
        touch-action: pan-y;
        background: ${Colors.bg};
      }
      #root > * {
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }
      @supports not (height: 100dvh) {
        body, #root { min-height: 100vh; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      style.remove();
      document.removeEventListener('touchmove', preventMultiTouchZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
      document.removeEventListener('wheel', preventCtrlWheelZoom);
    };
  }, []);

  useEffect(() => {
    // While booting, the light splash owns the chrome; switch only once we know the screen.
    if (loading) return;
    const next = session ? LIGHT_CHROME : AUTH_CHROME;
    setChrome(next);
    setWebChromeColor(next.backgroundColor);
  }, [session, loading]);

  useEffect(() => {
    const wasOffline = previousOnline.current === false;
    previousOnline.current = online;
    if (session && online && wasOffline) void useStore.getState().fetchAll();
  }, [online, session]);

  // The dashboard used to be responsible for the first data fetch. Since the
  // Assistant is now the initial screen, bootstrap the shared store at the
  // authenticated shell boundary instead of relying on a hidden legacy route.
  useEffect(() => {
    if (session) void useStore.getState().fetchAll();
  }, [session]);

  useEffect(() => {
    if (!session || storeLoading) return;
    void checkAndNotifyUpcomingExpenses(useStore.getState().getAllLaunches());
  }, [session, storeLoading]);

  useEffect(() => {
    if (!session || !DEFAULT_HOUSE_ID) return;
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;
    const debounceRefetch = () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => {
        const { selectedMonth, selectedYear } = useStore.getState();
        void useStore.getState().fetchMonthData(selectedMonth, selectedYear);
      }, 300);
    };
    const filter = `house_id=eq.${DEFAULT_HOUSE_ID}`;
    const channel = supabase
      .channel(`house-realtime-${DEFAULT_HOUSE_ID}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'one_off_expenses', filter }, debounceRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'recurring_occurrences', filter }, debounceRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'income', filter }, debounceRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'installment_occurrences', filter }, debounceRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices', filter }, debounceRefetch)
      .subscribe();
    return () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      void supabase.removeChannel(channel);
    };
  }, [session]);

  useEffect(() => {
    if (Platform.OS !== 'web' || loading || typeof document === 'undefined') return;

    const splash = document.getElementById('ced-splash');
    if (!splash) return;

    splash.classList.add('is-ready');
    const removeSplash = () => splash.remove();
    const timeoutId = window.setTimeout(removeSplash, 220);
    splash.addEventListener('transitionend', removeSplash, { once: true });

    return () => {
      window.clearTimeout(timeoutId);
      splash.removeEventListener('transitionend', removeSplash);
    };
  }, [fontsLoaded, loading, viewportReady]);

  // Web font loading and viewport measurement are progressive enhancements;
  // they must not keep the React tree empty if a browser blocks a font or
  // does not expose a stable visualViewport (the static splash already covers
  // the first paint). Native still waits for both before leaving its boot UI.
  const nativeBootBlocked = Platform.OS !== 'web' && (!viewportReady || !fontsLoaded);
  if (loading || nativeBootBlocked) {
    if (Platform.OS === 'web') return null;

    return (
      <View style={styles.bootSplash}>
        <StatusBar barStyle="dark-content" backgroundColor={Colors.bg} />
        <View style={styles.bootMark}><Ionicons name="home-outline" size={32} color="#FFFFFF" /></View>
        <Text style={styles.bootTitle}>Casa em Dia</Text>
        <Text style={styles.bootSubtitle}>Finanças do casal, em dia.</Text>
        <ActivityIndicator color={Colors.primary} />
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={styles.root}>
      <PhoneShell>
        <SafeAreaProvider>
          <StatusBar barStyle={chrome.barStyle} backgroundColor={chrome.backgroundColor} />
          {session && (!online || isOffline) ? <OfflineBanner lastSyncedAt={lastSyncedAt} /> : null}
          {session ? (
            <NavigationContainer
              onReady={() => {
                const next = chromeForRoute('Assistente');
                setChrome(next);
                setWebChromeColor(next.backgroundColor);
              }}
              onStateChange={state => {
                if (!state) return;
                const next = chromeForRoute(state.routes[state.index]?.name);
                setChrome(next);
                setWebChromeColor(next.backgroundColor);
              }}
            >
              <TabNavigator />
            </NavigationContainer>
          ) : <AuthScreen />}
        </SafeAreaProvider>
      </PhoneShell>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, width: '100%', minHeight: '100%' },
  bootSplash: {
    flex: 1,
    width: '100%',
    backgroundColor: Colors.bg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bootMark: {
    width: 72,
    height: 72,
    borderRadius: 23,
    backgroundColor: Colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 13 },
    shadowOpacity: 0.28,
    shadowRadius: 30,
    elevation: 8,
  },
  bootTitle: { color: Colors.text1, fontSize: 25, fontWeight: '800', letterSpacing: -0.72 },
  bootSubtitle: { color: Colors.text2, fontSize: 14, marginTop: 5, marginBottom: 32 },
});
